import clsx from 'clsx';
import Downshift from 'downshift';
import { ForwardedRef, forwardRef, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { CaretDownIcon } from '~components/icons';
import { Button } from '../Button/Button';
import styles from '../Editor.module.css';

export type ComplexSelectItem = { value: string; label?: string };
export type SelectItem = string | ComplexSelectItem;

export type SelectProps = {
  placeholder?: string;
  items: SelectItem[];
  selectedValue?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
};

const isComplexSelectItem = (toBeChecked: SelectItem): toBeChecked is ComplexSelectItem =>
  typeof toBeChecked !== 'string';

// const itemToString = (item: SelectItem) => (item ? (isComplexSelectItem(item) ? item.value : item) : '');
const itemToValue = (item: SelectItem) => (item ? (isComplexSelectItem(item) ? item.value : item) : '');
// const itemToLabel = (item: SelectItem) => (item ? (isComplexSelectItem(item) ? item.label || item.value : item) : '');
const labelFromValue = (items: SelectItem[], value: string): string => {
  const item = items.find(item => {
    if (isComplexSelectItem(item)) {
      return item.value === value;
    } else {
      return item === value;
    }
  });
  return (item as ComplexSelectItem).label || (item as ComplexSelectItem).value || (item as string);
};

export const Select = ({
  items,
  placeholder = 'btnSelectPlaceholder',
  onChange,
  defaultValue,
  selectedValue,
  disabled = false,
}: SelectProps) => {
  const handleOnChange = useCallback(
    (selection: string) => {
      if (onChange) {
        onChange(selection);
      }
    },
    [onChange],
  );

  return (
    <Downshift
      onChange={handleOnChange}
      itemToString={itemToValue}
      selectedItem={selectedValue}
      initialSelectedItem={defaultValue}
    >
      {({ getItemProps, getMenuProps, getToggleButtonProps, isOpen, highlightedIndex, selectedItem, getRootProps }) => {
        const value = selectedItem ? labelFromValue(items, selectedItem) : placeholder;
        return (
          <div className={styles.select} {...getRootProps({}, { suppressRefError: true })}>
            <FormattedMessage id={value} defaultMessage={value}>
              {(msg: any) => (
                <Button
                  data-testid="select-button"
                  label={msg}
                  icon={<CaretDownIcon />}
                  disabled={disabled}
                  splitIcon
                  {...getToggleButtonProps()}
                />
              )}
            </FormattedMessage>

            <div {...getMenuProps()} className={clsx(styles.selectMenu, isOpen && styles.selectMenuOpen)}>
              <ul className={styles.selectMenuList}>
                {isOpen &&
                  items.map((item, index) => (
                    <SelectOption
                      key={`${index}`}
                      item={item}
                      hasFocus={highlightedIndex === index}
                      {...getItemProps({ item: isComplexSelectItem(item) ? item.value : item, index })}
                    />
                  ))}
              </ul>
            </div>
          </div>
        );
      }}
    </Downshift>
  );
};

const SelectOption = forwardRef(
  (
    { item, hasFocus, ...passThroughProps }: { item: SelectItem; hasFocus: boolean },
    ref: ForwardedRef<HTMLLIElement>,
  ) => {
    if (isComplexSelectItem(item)) {
      return (
        <li
          ref={ref}
          className={clsx(styles.selectMenuItem, hasFocus && styles.selectMenuItemFocus)}
          {...passThroughProps}
        >
          <FormattedMessage id={item.label || item.value} defaultMessage={item.label || item.value} />
        </li>
      );
    } else {
      return (
        <li
          ref={ref}
          className={clsx(styles.selectMenuItem, hasFocus && styles.selectMenuItemFocus)}
          {...passThroughProps}
        >
          <FormattedMessage id={item} defaultMessage={item} />
        </li>
      );
    }
  },
);
SelectOption.displayName = 'SelectOption';
